import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUser } from '../UserContext';
import "./index.css";
import { FcCancel, FcPlus, FcMinus, FcPrint } from "react-icons/fc";
import logoNfce from "../../imagens/nfce-logo.png"

const fmt = (v) => Number(v || 0).toFixed(2).replace(".", ",");

// Corrige encoding quebrado que vem da API (UTF-8 mal interpretado)
const corrigirEncoding = (texto) => {
  return texto
    .replace(/Ã‰/g, "É")
    .replace(/Ã©/g, "é")
    .replace(/Ã‡/g, "Ç")
    .replace(/Ã§/g, "ç")
    .replace(/Ã/g, "Á");
};

// Converte "PIX: R$ 59.00,DÃBITO: R$ 10.00" em [{ forma: "PIX", valor: 59 }, ...]
const parsePagamentos = (str) => {
  if (!str) return [];
  return str
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const [formaRaw, valorRaw] = p.split(":");
      const forma = corrigirEncoding((formaRaw || "").trim());
      const valorNum = parseFloat(
        String(valorRaw || "0").replace(/[^\d.,]/g, "").replace(",", ".")
      ) || 0;
      return { forma, valor: valorNum };
    });
};

const hojeISO = () => {
  const d = new Date();
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

const exportarCSV = (linhas, colunas, nomeArquivo) => {
  const header = colunas.map((c) => c.label).join(";");
  const rows = linhas.map((linha) =>
    colunas.map((c) => `"${String(c.valor(linha)).replace(/"/g, '""')}"`).join(";")
  );
  const csvContent = [header, ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${nomeArquivo}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function ModalCaixa({ onClose }) {
  const { userInfo } = useUser();
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [dataInicio, setDataInicio] = useState(hojeISO());
  const [dataFim, setDataFim] = useState(hojeISO());
  const [linhaExpandida, setLinhaExpandida] = useState(null); // cupId da linha aberta
  const [itensPorVenda, setItensPorVenda] = useState({}); // cache: { [cupId]: [itens] }
  const [loadingItens, setLoadingItens] = useState(null); // cupId sendo carregado

  const credentials = btoa(`${userInfo.alias};${userInfo.venLogin}:${userInfo.venSenha}`);

  const fetchVendas = useCallback(async () => {
    if (!userInfo?.url || !userInfo?.alias) return;

    try {
      setLoading(true);
      setErro("");

      const res = await fetch(`${userInfo.url}/nfce`, {
        headers: {
          "Content-Type": "application/json",
          empresa: userInfo.alias,
          Authorization: `Basic ${credentials}`,
        },
      });

      if (!res.ok) {
        console.error("Erro ao buscar vendas:", res.status, await res.text());
        setErro("Erro ao buscar vendas.");
        return;
      }

      const text = await res.text();
      const json = JSON.parse(text);
      setVendas(Array.isArray(json) ? json : []);
    } catch (err) {
      console.error("Erro ao buscar vendas:", err);
      setErro("Erro ao buscar vendas.");
    } finally {
      setLoading(false);
    }
  }, [userInfo, credentials]);

  useEffect(() => {
    fetchVendas();
  }, [fetchVendas]);

  /*Busca por <item></item>*/
  const buscarItens = async (cupId) => {
    if (!cupId) {
      console.error("ID da venda não fornecido.");
      return;
    }

    // Se já está expandida, apenas fecha
    if (linhaExpandida === cupId) {
      setLinhaExpandida(null);
      return;
    }

    // Se já tem no cache, só expande sem buscar de novo
    if (itensPorVenda[cupId]) {
      setLinhaExpandida(cupId);
      return;
    }

    try {
      setLoadingItens(cupId);

      const response = await fetch(`${userInfo.url}/itemnfce/pedido/${cupId}`, {
        headers: {
          "Content-Type": "application/json",
          empresa: userInfo.alias,
          Authorization: `Basic ${credentials}`,
        },
      });

      if (!response.ok) {
        console.error("Erro ao buscar itens:", response.status, await response.text());
        return;
      }

      const text = await response.text();
      const json = JSON.parse(text);

      setItensPorVenda((prev) => ({ ...prev, [cupId]: Array.isArray(json) ? json : [] }));
      setLinhaExpandida(cupId);
    } catch (error) {
      console.error("Erro ao buscar itens:", error);
    } finally {
      setLoadingItens(null);
    }
  };

  // ---------- Filtragem por data ----------
  const extrairDataISO = (isoString) => {
    if (!isoString) return "";
    return isoString.split("T")[0];
  };

  const formatarDataBR = (isoString) => {
    const data = extrairDataISO(isoString);
    if (!data) return "";
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  const vendasFiltradas = useMemo(() => {
    if (!dataInicio || !dataFim) return vendas;

    return vendas.filter((v) => {
      const dataVenda = extrairDataISO(v.cupDtaCadastro);
      return dataVenda >= dataInicio && dataVenda <= dataFim;
    });
  }, [vendas, dataInicio, dataFim]);

  // ---------- Resumo inteligente ----------
  const resumo = useMemo(() => {
    const totalVendas = vendasFiltradas.reduce((sum, v) => sum + Number(v.cupTotCupom || 0), 0);
    const totalDesconto = vendasFiltradas.reduce((sum, v) => sum + Number(v.cupTotDesconto || 0), 0);
    const qtdVendas = vendasFiltradas.length;

    const porForma = {};
    vendasFiltradas.forEach((v) => {
      const pagamentos = parsePagamentos(v.pagamentos);
      pagamentos.forEach(({ forma, valor }) => {
        porForma[forma] = (porForma[forma] || 0) + valor;
      });
    });

    const listaFormas = Object.entries(porForma)
      .map(([forma, valor]) => ({ forma, valor }))
      .sort((a, b) => b.valor - a.valor);

    return { totalVendas, totalDesconto, qtdVendas, listaFormas };
  }, [vendasFiltradas]);

  // ---------- Impressão ----------
  const imprimirLista = () => {
    const win = window.open("", "_blank", "width=700,height=800");
    win.document.write(`
      <html>
      <head>
        <title>Relatório de Vendas</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; padding: 16px; }
          h2 { margin: 0 0 4px; }
          .periodo { margin-bottom: 12px; color: #555; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
          th { background: #f2f3f5; }
          tfoot td { font-weight: bold; background: #f8f9fa; }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        <h2>${userInfo.alias}</h2>
        <div class="periodo">Período: ${dataInicio.split("-").reverse().join("/")} até ${dataFim.split("-").reverse().join("/")}</div>
        <table>
          <thead>
            <tr>
              <th>Cupom</th>
              <th>Data</th>
              <th>Status</th>
              <th>Mercadoria</th>
              <th>Desconto</th>
              <th>Total</th>
              <th>Pagamentos</th>
            </tr>
          </thead>
          <tbody>
            ${vendasFiltradas
              .map(
                (v) => `
              <tr>
                <td>${v.cupId}</td>
                <td>${formatarDataBR(v.cupDtaCadastro)}</td>
                <td>${v.cupStatus}</td>
                <td>R$ ${fmt(v.cupTotMercadoria)}</td>
                <td>R$ ${fmt(v.cupTotDesconto)}</td>
                <td>R$ ${fmt(v.cupTotCupom)}</td>
                <td>${corrigirEncoding(v.pagamentos || "")}</td>
              </tr>`
              )
              .join("")}
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5">TOTAL (${vendasFiltradas.length} vendas)</td>
              <td>R$ ${fmt(resumo.totalVendas)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </body>
      </html>
    `);
    win.document.close();
  };

  const imprimirResumo = () => {
    const win = window.open("", "_blank", "width=500,height=700");
    win.document.write(`
      <html>
      <head>
        <title>Resumo de Vendas</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 13px; padding: 16px; }
          h2 { margin: 0 0 4px; }
          .periodo { margin-bottom: 16px; color: #555; }
          .linha { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #eee; }
          .total { font-size: 16px; font-weight: bold; margin-top: 12px; }
        </style>
      </head>
      <body onload="window.print(); window.close();">
        <h2>${userInfo.alias} - Resumo</h2>
        <div class="periodo">Período: ${dataInicio.split("-").reverse().join("/")} até ${dataFim.split("-").reverse().join("/")}</div>
        <div class="linha"><span>Quantidade de vendas</span><strong>${resumo.qtdVendas}</strong></div>
        <div class="linha"><span>Total de desconto</span><strong>R$ ${fmt(resumo.totalDesconto)}</strong></div>
        <hr/>
        <h3>Por forma de pagamento</h3>
        ${resumo.listaFormas
          .map(
            (f) => `<div class="linha"><span>${f.forma}</span><strong>R$ ${fmt(f.valor)}</strong></div>`
          )
          .join("")}
        <div class="total linha"><span>TOTAL GERAL</span><span>R$ ${fmt(resumo.totalVendas)}</span></div>
      </body>
      </html>
    `);
    win.document.close();
  };

  // ---------- Exportação Excel (CSV) ----------
  const exportarListaExcel = () => {
    exportarCSV(
      vendasFiltradas,
      [
        { label: "Cupom", valor: (v) => v.cupId },
        { label: "Data", valor: (v) => formatarDataBR(v.cupDtaCadastro) },
        { label: "Status", valor: (v) => v.cupStatus },
        { label: "Mercadoria", valor: (v) => fmt(v.cupTotMercadoria) },
        { label: "Desconto", valor: (v) => fmt(v.cupTotDesconto) },
        { label: "Total", valor: (v) => fmt(v.cupTotCupom) },
        { label: "Pagamentos", valor: (v) => corrigirEncoding(v.pagamentos || "") },
      ],
      `vendas_${dataInicio}_a_${dataFim}`
    );
  };

  const exportarResumoExcel = () => {
    const linhas = [
      { label: "Quantidade de vendas", valor: resumo.qtdVendas },
      { label: "Total de desconto", valor: fmt(resumo.totalDesconto) },
      ...resumo.listaFormas.map((f) => ({ label: f.forma, valor: fmt(f.valor) })),
      { label: "TOTAL GERAL", valor: fmt(resumo.totalVendas) },
    ];

    exportarCSV(
      linhas,
      [
        { label: "Descrição", valor: (l) => l.label },
        { label: "Valor", valor: (l) => l.valor },
      ],
      `resumo_${dataInicio}_a_${dataFim}`
    );
  };

  // ---------- Reimpressão de ticket de uma venda específica ----------
  const reimprimirTicket = async (venda) => {
    // Abre a aba IMEDIATAMENTE, ainda dentro do clique do usuário
    const win = window.open("", "_blank", "width=600,height=600");

    if (!win) {
      alert("O navegador bloqueou a abertura da aba. Permita pop-ups para este site.");
      return;
    }

    win.document.write("<p style='font-family:sans-serif;padding:20px;'>Carregando ticket...</p>");

    try {
      let itens = itensPorVenda[venda.cupId];

      if (!itens) {
        const response = await fetch(`${userInfo.url}/itemnfce/pedido/${venda.cupId}`, {
          headers: {
            "Content-Type": "application/json",
            empresa: userInfo.alias,
            Authorization: `Basic ${credentials}`,
          },
        });

        if (!response.ok) {
          console.error("Erro ao buscar itens para reimpressão:", response.status, await response.text());
          win.close();
          alert("Erro ao buscar itens da venda.");
          return;
        }

        const text = await response.text();
        itens = JSON.parse(text);
        setItensPorVenda((prev) => ({ ...prev, [venda.cupId]: itens }));
      }

      const pagamentos = parsePagamentos(venda.pagamentos);
      const dataObj = venda.cupDtaEmissao
        ? new Date(venda.cupDtaEmissao.replace(/Z$/, "")) // remove o Z → vira hora local
        : new Date();

      const dataFormatada = dataObj.toLocaleDateString("pt-BR");
      const horaFormatada = dataObj.toLocaleTimeString("pt-BR");

      const totalMercadoria = Number(venda.cupTotMercadoria || 0);
      const totalDesconto = Number(venda.cupTotDesconto || 0);
      const totalCupom = Number(venda.cupTotCupom || 0);
      const totalRecebido = Number(venda.cupTotRecebido || 0);
      const troco = Number(venda.cupTotTroco || 0);

      win.document.open();
      win.document.write(`
        <html>
        <head>
          <title>Ticket de Venda - Cupom ${venda.cupId}</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            html, body { width: 80mm; margin: 0; padding: 0; }
            body {
              font-family: monospace;
              font-size: 12px;
              padding: 2mm 3mm;
              box-sizing: border-box;
            }
            .logo { display:block; margin: 0 auto 8px; max-width: 100%; }
            h2 { text-align:center; margin: 4px 0; }
            .datahora { text-align:center; margin-bottom: 6px; }
            .reimpressao { text-align:center; font-weight:bold; margin-bottom: 6px; }
            .linha { display:flex; justify-content:space-between; word-break: break-word; }
            hr { border: none; border-top: 1px dashed #000; }
            .total { font-size: 14px; font-weight:bold; }
            .pagamentos { margin-top: 4px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <img class="logo" src="${userInfo.url}/logos/${userInfo.alias}/${userInfo.alias}.bmp" />
          <h2>${userInfo.company}</h2>
          <div class="reimpressao">--- REIMPRESSÃO ---</div>
          <div class="datahora">Cupom ${venda.cupId} — ${dataFormatada} ${horaFormatada}</div>
          <hr/>
          ${itens
            .map(
              (it) => `
            <div class="linha"><span>${it.icuQtdProduto}x ${it.proDescricao}</span><span>R$ ${fmt(
                it.icuQtdProduto * it.icuVlrProduto
              )}</span></div>`
            )
            .join("")}
          <hr/>
          <div class="linha"><span>Subtotal</span><span>R$ ${fmt(totalMercadoria)}</span></div>
          <div class="linha"><span>Desconto</span><span>R$ ${fmt(totalDesconto)}</span></div>
          <div class="linha total"><span>TOTAL</span><span>R$ ${fmt(totalCupom)}</span></div>
          <hr/>
          <div class="pagamentos">
            ${pagamentos
              .map(
                (p) => `
              <div class="linha"><span>${p.forma}</span><span>R$ ${fmt(p.valor)}</span></div>`
              )
              .join("")}
          </div>
          <hr/>
          <div class="linha"><span>Total Recebido</span><span>R$ ${fmt(totalRecebido)}</span></div>
          ${
            troco > 0
              ? `<div class="linha total"><span>TROCO</span><span>R$ ${fmt(troco)}</span></div>`
              : ""
          }
          <hr/>
          <p style="text-align:center;">Obrigado pela preferência!</p>
        </body>
        </html>
      `);
      win.document.close();
    } catch (err) {
      console.error("Erro ao reimprimir ticket:", err);
      win.close();
      alert("Erro ao reimprimir ticket.");
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box modal-caixa">
        <div className="modal-header">
          <h3>Vendas do Caixa</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="caixa-filtros">
          <div className="caixa-filtro-item">
            <label>Data Início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          </div>
          <div className="caixa-filtro-item">
            <label>Data Fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </div>
          <button className="btn-refresh" onClick={fetchVendas}>
            ⟳ Atualizar
          </button>
        </div>

        {erro && <div className="caixa-erro">{erro}</div>}
        {loading && <div className="caixa-loading">Carregando vendas...</div>}

        <div className="caixa-content">
          {/* LADO ESQUERDO - LISTA */}
          <div className="caixa-lista">
            <div className="caixa-lista-header">
              <span>{vendasFiltradas.length} venda(s)</span>
              <div className="caixa-lista-actions">
                <button className="btn-export" onClick={exportarListaExcel}>
                  Exportar Excel
                </button>
                <button className="btn-print" onClick={imprimirLista}>
                  🖨 Imprimir
                </button>
              </div>
            </div>

            <div className="caixa-tabela-wrapper">
              <table className="caixa-tabela">
                <thead>
                  <tr>
                    <th>Cupom</th>
                    <th>Data</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Pagamentos</th>
                    <th>Reimprimir</th>
                    <th>Emitir NFCe</th>
                    <th>Cancelar Venda</th>
                    <th>Produtos</th>
                  </tr>
                </thead>
                <tbody>
                  {vendasFiltradas.map((v) => (
                    <React.Fragment key={v.cupId}>
                      <tr>
                        <td>{v.cupId}</td>
                        <td>{formatarDataBR(v.cupDtaCadastro)}</td>
                        <td>
                          <span className={`status-badge status-${v.cupStatus?.toLowerCase()}`}>
                            {v.cupStatus}
                          </span>
                        </td>
                        <td>R$ {fmt(v.cupTotCupom)}</td>
                        <td className="caixa-pagamentos-cell">
                          {corrigirEncoding(v.pagamentos || "")}
                        </td>
                        <td className="icones">
                          <button className="btn-icone" onClick={() => reimprimirTicket(v)} title="Reimprimir cupom">
                            <FcPrint size={18} />
                          </button>
                        </td>
                        <td className="icones"><img src={logoNfce} alt="nfce" /></td>
                        <td className="icones"><FcCancel size={18} /></td>
                        <td className="icones">
                          <button
                            className={`btn-expandir ${linhaExpandida === v.cupId ? "ativo" : ""}`}
                            onClick={() => buscarItens(v.cupId)}
                            disabled={loadingItens === v.cupId}
                          >
                            {loadingItens === v.cupId ? "..." : linhaExpandida === v.cupId ? <FcMinus size={18} /> : <FcPlus size={18} />}
                          </button>
                        </td>
                      </tr>

                      {linhaExpandida === v.cupId && (
                        <tr className="linha-itens">
                          <td colSpan={8}>
                            <div className="itens-expandido">
                              <table className="itens-tabela">
                                <thead>
                                  <tr>
                                    <th>Produto</th>
                                    <th>Qtd</th>
                                    <th>Valor Unit.</th>
                                    <th>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(itensPorVenda[v.cupId] || []).length === 0 ? (
                                    <tr>
                                      <td colSpan={4} className="caixa-empty">
                                        Nenhum item encontrado.
                                      </td>
                                    </tr>
                                  ) : (
                                    itensPorVenda[v.cupId].map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.proDescricao}</td>
                                        <td>{item.icuQtdProduto}</td>
                                        <td>R$ {fmt(item.icuVlrProduto)}</td>
                                        <td>R$ {fmt(item.icuQtdProduto * item.icuVlrProduto)}</td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                </tbody>
              </table>
            </div>
          </div>

          {/* LADO DIREITO - RESUMO */}
          <div className="caixa-resumo">
            <div className="caixa-resumo-header">
              <h4>Resumo</h4>
              <div className="caixa-lista-actions">
                <button className="btn-export" onClick={exportarResumoExcel}>
                  Excel
                </button>
                <button className="btn-print" onClick={imprimirResumo}>
                  🖨
                </button>
              </div>
            </div>

            <div className="resumo-card">
              <span>Quantidade de vendas</span>
              <strong>{resumo.qtdVendas}</strong>
            </div>

            <div className="resumo-card">
              <span>Total de desconto</span>
              <strong>R$ {fmt(resumo.totalDesconto)}</strong>
            </div>

            <div className="resumo-divider" />

            <h5>Por forma de pagamento</h5>
            <div className="resumo-formas">
              {resumo.listaFormas.length === 0 ? (
                <div className="caixa-empty">Sem pagamentos no período.</div>
              ) : (
                resumo.listaFormas.map((f) => (
                  <div key={f.forma} className="resumo-forma-item">
                    <span>{f.forma}</span>
                    <strong>R$ {fmt(f.valor)}</strong>
                  </div>
                ))
              )}
            </div>

            <div className="resumo-total">
              <span>TOTAL GERAL</span>
              <strong>R$ {fmt(resumo.totalVendas)}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}